const STORAGE_KEYS = {
  enabled: 'youthProtectionEnabled',
  passwordHash: 'youthProtectionPasswordHash',
  usageDate: 'youthProtectionUsageDate',
  usedMs: 'youthProtectionUsedMs',
  lastActiveAt: 'youthProtectionLastActiveAt'
};

export const YOUTH_PROTECTION_LIMIT_MS = 2 * 60 * 60 * 1000;

export const getTodayKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStorageNumber = (key) => {
  const value = Number(localStorage.getItem(key));
  return Number.isFinite(value) ? value : 0;
};

const setStorageNumber = (key, value) => {
  localStorage.setItem(key, String(Math.max(0, Math.floor(value))));
};

const fallbackHash = (value) => {
  const normalized = encodeURIComponent(value)
    .split('')
    .reduce((result, char) => result + char.charCodeAt(0).toString(16), '');
  return `fallback:${normalized}`;
};

export const hashYouthProtectionPassword = async (password) => {
  if (!window.crypto?.subtle) {
    return fallbackHash(password);
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const buffer = await window.crypto.subtle.digest('SHA-256', data);
  const bytes = Array.from(new Uint8Array(buffer));
  return bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const normalizeDailyUsage = () => {
  const today = getTodayKey();
  const savedDate = localStorage.getItem(STORAGE_KEYS.usageDate);

  if (savedDate !== today) {
    localStorage.setItem(STORAGE_KEYS.usageDate, today);
    setStorageNumber(STORAGE_KEYS.usedMs, 0);
    setStorageNumber(STORAGE_KEYS.lastActiveAt, Date.now());
  }
};

export const isYouthProtectionEnabled = () => {
  return localStorage.getItem(STORAGE_KEYS.enabled) === '1';
};

export const setYouthProtectionLastActiveAt = (timestamp = Date.now()) => {
  if (!isYouthProtectionEnabled()) {
    return;
  }

  normalizeDailyUsage();
  setStorageNumber(STORAGE_KEYS.lastActiveAt, timestamp);
};

export const settleYouthProtectionUsage = (options = {}) => {
  if (!isYouthProtectionEnabled()) {
    return getYouthProtectionStatus();
  }

  normalizeDailyUsage();

  const now = Date.now();
  const lastActiveAt = getStorageNumber(STORAGE_KEYS.lastActiveAt) || now;
  const shouldCount = options.force || document.visibilityState === 'visible';
  const usedMs = getStorageNumber(STORAGE_KEYS.usedMs);

  if (!shouldCount) {
    setStorageNumber(STORAGE_KEYS.lastActiveAt, now);
    return getYouthProtectionStatus();
  }

  const elapsedMs = Math.max(0, now - lastActiveAt);
  const nextUsedMs = Math.min(YOUTH_PROTECTION_LIMIT_MS, usedMs + elapsedMs);

  setStorageNumber(STORAGE_KEYS.usedMs, nextUsedMs);
  setStorageNumber(STORAGE_KEYS.lastActiveAt, now);

  return getYouthProtectionStatus();
};

export const getYouthProtectionStatus = () => {
  normalizeDailyUsage();

  const enabled = isYouthProtectionEnabled();
  const usedMs = enabled ? getStorageNumber(STORAGE_KEYS.usedMs) : 0;
  const remainingMs = Math.max(0, YOUTH_PROTECTION_LIMIT_MS - usedMs);

  return {
    enabled,
    usedMs,
    remainingMs,
    limitMs: YOUTH_PROTECTION_LIMIT_MS,
    isLocked: enabled && remainingMs <= 0,
    date: localStorage.getItem(STORAGE_KEYS.usageDate) || getTodayKey()
  };
};

export const enableYouthProtection = async (password) => {
  normalizeDailyUsage();
  const passwordHash = await hashYouthProtectionPassword(password);

  localStorage.setItem(STORAGE_KEYS.enabled, '1');
  localStorage.setItem(STORAGE_KEYS.passwordHash, passwordHash);
  setStorageNumber(STORAGE_KEYS.lastActiveAt, Date.now());

  return getYouthProtectionStatus();
};

export const verifyYouthProtectionPassword = async (password) => {
  const savedHash = localStorage.getItem(STORAGE_KEYS.passwordHash);

  if (!savedHash) {
    return false;
  }

  const passwordHash = await hashYouthProtectionPassword(password);
  return passwordHash === savedHash;
};

export const disableYouthProtection = async (password) => {
  const verified = await verifyYouthProtectionPassword(password);

  if (!verified) {
    return false;
  }

  localStorage.removeItem(STORAGE_KEYS.enabled);
  localStorage.removeItem(STORAGE_KEYS.passwordHash);
  localStorage.removeItem(STORAGE_KEYS.lastActiveAt);

  return true;
};

export const formatYouthProtectionDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}小时${String(minutes).padStart(2, '0')}分钟${String(seconds).padStart(2, '0')}秒`;
  }

  return `${minutes}分钟${String(seconds).padStart(2, '0')}秒`;
};
